import { Builder } from '@builder.io/react';
import cn from 'classnames';
import capitalize from 'lodash-es/capitalize';
import { FC } from 'react';
import { match } from 'ts-pattern';

import {
  ALLOW_BACKGROUND_COLORS,
  COLOR_NAME_BLACK,
  COLOR_NAME_BRAND,
  COLOR_NAME_LIGHT_GRAY,
  COLOR_NAME_PALE_GRAY,
  COLOR_NAME_WHITE,
} from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import {
  TOP_OF_THE_PAGE_SPACING,
  getBlockColor,
  getBlockIndex,
  getBuilderSpacingClass,
  getNextBlock,
} from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { isInMobileApp } from '@/shared/utils/isInMobileApp';

import { ActionVariant, BaseAction } from '../Action';
import { Media, MediaInput, mediaInput } from '../Media';
import sharedStyle from '../shared.module.scss';
import { Content, ContentType } from './Content';
import style from './Hero.module.scss';

export const HERO_STANDARD_SPACING =
  'tw:xl:pt-xxl-6 tw:pt-xxl-10 tw:xl:pb-xxl-18 tw:pb-xxl-10';
export const HERO_CONTENT_SPACING = 'tw:xl:py-xxl-18 tw:py-xxl-10';
export const HERO_ACTIVATION_SPACING =
  'tw:xl:mt-xxl-6 tw:sm:mt-xl tw:sm:py-xxl-6 tw:sm:mb-m';
export const HERO_ACTIVATION_SPACING_IN_MOBILE_APP =
  'tw:sm:py-xxl-6 tw:sm:mb-m';

interface BuilderInput extends SharedBuilderComponent, MediaInput {
  componentLinkId?: string;
  type?: ContentType;
  headline?: string;
  subHeadline?: string;
  description?: string;
  linkToId?: string;
  action?: BaseAction;
  actionVariant?: ActionVariant;
}

type Props = BuilderInput & {
  locale: string;
};

export const Hero: FC<Props> = ({
  type = ContentType.STANDARD,
  componentLinkId,
  headline,
  subHeadline,
  description,
  linkToId,
  action,
  actionVariant = ActionVariant.NONE,
  mediaType,
  mediaCaption,
  video,
  image,
  imageLink,
  imageLinkTarget,
  youtube,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = match({
    index: getBlockIndex(builderBlock, builderState),
    type,
  })
    .with({ index: 0, type: ContentType.STANDARD }, () => HERO_STANDARD_SPACING)
    .with({ index: 0, type: ContentType.CONTENT }, () => HERO_CONTENT_SPACING)
    .with({ index: 0, type: ContentType.ACTIVATIONS }, () => {
      const nextComponent = getNextBlock(builderBlock, builderState);
      if (getBlockColor(nextComponent) === getBlockColor(builderBlock)) {
        return TOP_OF_THE_PAGE_SPACING;
      }
      if (isInMobileApp()) {
        return HERO_ACTIVATION_SPACING_IN_MOBILE_APP;
      }
      return HERO_ACTIVATION_SPACING;
    })
    .otherwise(() => getBuilderSpacingClass(builderBlock, builderState));

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;

  return (
    <section
      {...attributes}
      data-testid="hero"
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className={cn('u-wrapper')}>
        <div className={cn(style.hero, 'tw:flex')}>
          <Content
            locale={blockLocale}
            type={type}
            headline={headline}
            subHeadline={subHeadline}
            description={description}
            linkToId={linkToId}
            action={{ ...action, variant: actionVariant, locale: blockLocale }}
          />
          {type !== ContentType.CONTENT && (
            <Media
              className={cn(style.media)}
              locale={blockLocale}
              mediaType={mediaType}
              mediaCaption={mediaCaption}
              video={video}
              image={image}
              imageLink={imageLink}
              imageLinkTarget={imageLinkTarget}
              youtube={youtube}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'Hero';

export function componentRegister() {
  Builder.registerComponent(Hero, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Type',
        type: 'string',
        defaultValue: ContentType.STANDARD.toString(),
        enum: Object.values(ContentType).map((val) => ({
          label: capitalize(val),
          value: val,
        })),
        // Builder editor eval this function in the browser
        // so we can't use any variable reference here because
        // the other codes doesn't carry into the browser.
        onChange: `
          if (options.get('type') === 'standard') {
            options.set('backgroundColorClassName', 'u-bgcolor-primary');
            return;
          }
          if (
            options.get('type') === 'activations' &&
            options.get('backgroundColorClassName') === 'u-bgcolor-primary'
          ) {
            options.set('backgroundColorClassName', 'u-bgcolor-brand');
            options.set(
              'activationsBackgroundColorClassName',
              'u-bgcolor-brand'
            );
          }
        `,
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'CatawikiEditor',
        defaultValue:
          'Headline text <span class="u-typography-h1-serif u-color-brand">added here</span>',
      },
      {
        name: 'subHeadline',
        friendlyName: 'Sub Headline',
        type: 'CatawikiEditor',
        defaultValue: '14 - 18 Dec',
        showIf: `['activations', 'collections'].includes(options.get('type'))`,
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue:
          'Short or medium description and introduction of the content, short or medium description and introduction of the content.',
      },
      {
        name: 'linkToId',
        friendlyName: 'Link to Section ID',
        type: 'string',
        showIf: `options.get('type') === 'standard'`,
      },
      backgroundColorInput(COLOR_NAME_WHITE, {
        allowBackgroundColors: [
          ...ALLOW_BACKGROUND_COLORS,
          COLOR_NAME_BRAND,
          COLOR_NAME_BLACK,
        ],
        override: {
          showIf: `['content', 'collections'].includes(options.get('type'))`,
        },
      }),
      // Hero Activations does not have white background option however,
      // Builder doesn't allow different options for input base on condition.
      // so this is a duplicate background color with different input name
      // but will show only when Hero type is activations.
      backgroundColorInput(COLOR_NAME_BRAND, {
        allowBackgroundColors: [
          COLOR_NAME_LIGHT_GRAY,
          COLOR_NAME_PALE_GRAY,
          COLOR_NAME_BRAND,
          COLOR_NAME_BLACK,
        ],
        override: {
          name: 'activationsBackgroundColorClassName',
          showIf: `options.get('type') === 'activations'`,
          onChange: `
            options.set(
              'backgroundColorClassName',
              options.get('activationsBackgroundColorClassName')
            )
          `,
        },
      }),
      ...actionButtonInput({}),
      ...mediaInput({ showMobileSource: true, useCatawikiPlainEditor: true }),
    ],
    noWrap: true,
  });

  const v2Name = `v2:${NAME}`;
  Builder.registerComponent(Hero, {
    name: v2Name,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Type',
        type: 'string',
        defaultValue: ContentType.STANDARD.toString(),
        enum: Object.values(ContentType).map((val) => ({
          label: capitalize(val),
          value: val,
        })),
        // Builder editor eval this function in the browser
        // so we can't use any variable reference here because
        // the other codes doesn't carry into the browser.
        onChange: `
          if (options.get('type') === 'standard') {
            options.set('backgroundColorClassName', 'u-bgcolor-primary');
            return;
          }
          if (
            options.get('type') === 'activations' &&
            options.get('backgroundColorClassName') === 'u-bgcolor-primary'
          ) {
            options.set('backgroundColorClassName', 'u-bgcolor-brand');
            options.set(
              'activationsBackgroundColorClassName',
              'u-bgcolor-brand'
            );
          }
        `,
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'html',
        defaultValue:
          'Headline text <span class="u-typography-h1-serif u-color-brand">added here</span>',
      },
      {
        name: 'subHeadline',
        friendlyName: 'Sub Headline',
        type: 'html',
        defaultValue: '14 - 18 Dec',
        showIf: `['activations', 'collections'].includes(options.get('type'))`,
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'html',
        defaultValue:
          'Short or medium description and introduction of the content, short or medium description and introduction of the content.',
      },
      {
        name: 'linkToId',
        friendlyName: 'Link to Section ID',
        type: 'string',
        showIf: `options.get('type') === 'standard'`,
      },
      backgroundColorInput(COLOR_NAME_WHITE, {
        allowBackgroundColors: [
          ...ALLOW_BACKGROUND_COLORS,
          COLOR_NAME_BRAND,
          COLOR_NAME_BLACK,
        ],
        override: {
          showIf: `['content', 'collections'].includes(options.get('type'))`,
        },
      }),
      // Hero Activations does not have white background option however,
      // Builder doesn't allow different options for input base on condition.
      // so this is a duplicate background color with different input name
      // but will show only when Hero type is activations.
      backgroundColorInput(COLOR_NAME_BRAND, {
        allowBackgroundColors: [
          COLOR_NAME_LIGHT_GRAY,
          COLOR_NAME_PALE_GRAY,
          COLOR_NAME_BRAND,
          COLOR_NAME_BLACK,
        ],
        override: {
          name: 'activationsBackgroundColorClassName',
          showIf: `options.get('type') === 'activations'`,
          onChange: `
            options.set(
              'backgroundColorClassName',
              options.get('activationsBackgroundColorClassName')
            );
          `,
        },
      }),
      ...actionButtonInput({ useCatawikiPlainEditor: false }),
      ...mediaInput({ showMobileSource: true, useCatawikiPlainEditor: false }),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
